import * as _ from "lodash-es";

/**
 * Check if all values of object are `not true`.
 * @param {Object} obj
 */
export const isObjectValuesEmpty = (obj, keys = null) => {
  if (!obj) {
    return true;
  }
  keys = keys || Object.keys(obj);
  for (const key of keys) {
    const value = obj[key];
    if (Array.isArray(value)) {
      if (value.length) {
        return false;
      }
    } else if (_.isPlainObject(value)) {
      if (!isObjectValuesEmpty(value)) {
        return false;
      }
    } else if (value) {
      return false;
    }
  }
  return true;
};

/**
 * Check if all values of object are `true`.
 * @param {Object} obj
 */
export const isObjectFull = (obj) => {
  if (!obj) {
    return false;
  }
  for (const key in obj) {
    if (!obj[key]) {
      return false;
    }
  }
  return true;
};

/**
 * Add name to base64 representation of file to validate document's extension on backend.
 *
 * @param {File} file
 * @param {FileReader} fileReader
 *
 * @returns {String}
 */
export const addNameToBase64File = (file, fileReader) => {
  const [header, body] = fileReader.result.split(";base64,");
  return `${header};filename:${file.name};base64,${body || ""}`;
};

/**
 * Convert base64 to array of bytes
 * @param {String} base64
 */
export const base64ToBytes = (base64) => {
  const bytes = window.atob(base64);
  const array = new Uint8Array(new ArrayBuffer(bytes.length));
  for (let i = 0; i < bytes.length; i++) {
    array[i] = bytes.charCodeAt(i);
  }
  return array;
};

export const downloadUrl = (url, filename) => {
  const link = document.createElement("a");
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// TODO: parse "Content-Disposition" to get filename and "Content-Type" to get content type
export const downloadContent = (content, filename, contentType = null) => {
  // This function will not display that downloading was started until
  // all data is received.
  const blob = new Blob([content], contentType ? { type: contentType } : {});
  if (navigator.msSaveOrOpenBlob) {
    navigator.msSaveOrOpenBlob(blob, filename);
  } else {
    const url = URL.createObjectURL(blob);
    downloadUrl(url, filename);
    URL.revokeObjectURL(url);
  }
};

export const copyToClipboard = (str) => {
  const el = document.createElement("textarea");
  el.value = str;
  el.setAttribute("readonly", "");
  el.style.position = "absolute";
  el.style.left = "-9999px";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
};

export const slugify = (str) => {
  const a =
    "àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;";
  const b =
    "aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------";
  const p = new RegExp(a.split("").join("|"), "g");

  return str
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
    .replace(/&/g, "-and-") // Replace & with 'and'
    .replace(/[^\w-]+/g, "") // Remove all non-word characters
    .replace(/--+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, ""); // Trim - from end of text
};

/**
 * @param {String} text to split.
 * @returns {Array}
 */
export const splitWords = (text) => (text || "").split(/\s+/).filter((w) => w);

/**
 * @param {String}  src base64 data or url
 * @param {integer} degrees
 * @param {String}  outputFormat
 */
export function rotateImage(src, degrees, outputFormat = "image/jpeg") {
  return new Promise((resolve, reject) => {
    if (!_.isNumber(degrees)) {
      reject(`${degrees} is not a number.`);
    }

    // create an off-screen canvas
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // cteate Image
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = function () {
      // set its dimension to rotated size
      canvas.height = img.width;
      canvas.width = img.height;

      // rotate and draw source image into the off-screen canvas
      if (degrees > 0) {
        ctx.rotate((degrees * Math.PI) / 180);
        ctx.translate(0, -canvas.width);
      } else {
        ctx.rotate((degrees * Math.PI) / 180);
        ctx.translate(-canvas.height, 0);
      }
      ctx.drawImage(img, 0, 0);

      // encode image to data-uri with base64
      resolve(canvas.toDataURL(outputFormat, 100));
    };
    img.onerror = function (...args) {
      reject(args);
    };
    img.src = src;
  });
}

/**
 * Swap two positional elements of array and change their positions.
 *
 * @param {Array} data
 * @param {integer} firstIndex
 * @param {integer} secondIndex
 */
export const swapAndChangePosition = (data, firstPosition, secondPosition) => {
  const firstIndex = data.findIndex((v) => v.position === firstPosition);
  const secondIndex = data.findIndex((v) => v.position === secondPosition);
  const list = [...data];
  [list[firstIndex].position, list[secondIndex].position] = [
    list[secondIndex].position,
    list[firstIndex].position,
  ];
  [list[firstIndex], list[secondIndex]] = [list[secondIndex], list[firstIndex]];
  return list;
};

/**
 * Delete item with specified position and modify position of
 * remaining items to keep consistency.
 *
 * @param {Array} items List of items with `position` property
 * @param {Number} position Position of item to delete
 * @returns {Array}
 */
export const deleteItemWithPosition = (items, position) => {
  return items
    .filter((i) => i.position !== position)
    .map((i) => ({
      ...i,
      position: i.position > position ? i.position - 1 : i.position,
    }));
};

/**
 * @param {String} url
 * @returns {String}
 */
export const getFileExtensionFromUrl = (url) => {
  const fileName = url.split("?")[0].split("/").pop().toLowerCase();
  return fileName.split(".").pop();
};

/**
 * @param {String} url
 * @returns {String|null}
 */
export const getFormatFromDataURL = (url) => {
  const matches = url.match(/data:([^;]+)/);
  if (!matches) {
    return null;
  }
  return matches[1];
};

/**
 * Transform address to string.
 * @param {Object} address
 * @returns
 */
export const addressToString = (address) =>
  [
    address.apartment,
    address.street_address,
    address.suburb,
    address.state,
    address.postcode,
    address.country && address.country.name,
  ]
    .filter((s) => s)
    .join(" ");

export const insertScript = (src, params = {}) => {
  const script = document.createElement("script");
  script.async = true;
  script.defer = true;
  script.src = src;
  for (const [name, value] of Object.entries(params)) {
    script[name] = value;
  }
  document.querySelector("head").appendChild(script);
};

export const insertText = (textToInsert, text, position) => {
  return [text.slice(0, position), textToInsert, text.slice(position)].join("");
};

export const commaJoin = (value) => (value && value.length ? value.join(", ") : "");

/**
 * Join first n - 1 items with comma and last item with `and` prefix.
 * @param {Array} items
 * @returns {String}
 */
export const commaJoinEnd = (items) => {
  if (items.length === 0) {
    return "";
  }
  if (items.length === 1) {
    return String(items[0]);
  }
  const lastItem = items.slice(-1)[0];
  const restItems = items.slice(0, -1);
  return `${restItems.join(", ")} and ${lastItem}`;
};

export const deleteKeys = (obj, keys) => {
  if (!obj) {
    return;
  }
  keys.forEach((key) => {
    delete obj[key];
  });
};

export const isUUID = (str) =>
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi.test(
    str,
  );

export const generateUUID = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.trunc(Math.random() * 16);
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
